import { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useV2Auth } from '@/context/V2AuthContext';
import { Send, RefreshCw, Sparkles, Heart, ChevronDown, ChevronUp, Trophy, Pin, BookHeart, HeartHandshake, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
// PHASE 0: StreakWidget geparkt
// import { StreakWidget } from '@/components/StreakWidget';

/* Generate a deterministic daily index from the impulses array */
function getDailyIndex(arrayLength) {
  const now = new Date();
  const daySeed = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  let x = daySeed;
  x = ((x >> 16) ^ x) * 0x45d9f3b;
  x = ((x >> 16) ^ x) * 0x45d9f3b;
  x = (x >> 16) ^ x;
  return Math.abs(x) % arrayLength;
}

/* Pick N random items from array, excluding a specific index */
function pickRandom(arr, count, excludeIndex) {
  const indices = arr.map((_, i) => i).filter(i => i !== excludeIndex);
  const picked = [];
  while (picked.length < count && indices.length > 0) {
    const r = Math.floor(Math.random() * indices.length);
    picked.push(indices.splice(r, 1)[0]);
  }
  return picked.map(i => arr[i]);
}

export default function FeedV2() {
  const { user, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inspirationItems, setInspirationItems] = useState([]);
  const [gratitude, setGratitude] = useState([]);
  const [showGratitude, setShowGratitude] = useState(true);
  const [pinnedIds, setPinnedIds] = useState([]);
  const [reminder, setReminder] = useState(null);
  const [reminderDismissed, setReminderDismissed] = useState(false);

  // Daily impulse from translations — changes daily, always in current language
  const impulsesRaw = t('impulses', { returnObjects: true });
  const impulseKey = JSON.stringify(impulsesRaw);
  const impulses = useMemo(() => Array.isArray(impulsesRaw) ? impulsesRaw : [], [impulseKey]);
  const dailyImpulse = useMemo(() => {
    if (impulses.length === 0) return '';
    const idx = getDailyIndex(impulses.length);
    return impulses[idx];
  }, [impulses]);

  // Inspiration area: pick 3 random prompts (different from daily)
  const refreshInspiration = useCallback(() => {
    if (impulses.length < 4) return;
    const dailyIdx = getDailyIndex(impulses.length);
    setInspirationItems(pickRandom(impulses, 3, dailyIdx));
  }, [impulses]);

  useEffect(() => { refreshInspiration(); }, [refreshInspiration]);

  // Feed impulses from translations — for system items
  const feedImpulses = t('feedImpulses', { returnObjects: true });

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    let cancelled = false;
    async function loadFeed() {
      try {
        const [feedRes, gratRes, pinsRes, remRes] = await Promise.all([
          axios.get(`${API_V2}/impulse/feed`, { withCredentials: true }),
          axios.get(`${API_V2}/gratitude/feed`, { withCredentials: true }),
          axios.get(`${API_V2}/profile/pins`, { withCredentials: true }).catch(() => ({ data: { pinnedImpulses: [] } })),
          axios.get(`${API_V2}/herzensmenschen/reminders`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          setFeed(feedRes.data);
          setGratitude(gratRes.data);
          setPinnedIds((pinsRes.data.pinnedImpulses || []).map((p) => p.id));
          setReminder((remRes.data || [])[0] || null);
        }
      } catch (err) {
        if (!cancelled && err.response?.status === 401) { navigate('/auth', { replace: true }); return; }
        console.error('Feed load failed:', err);
      }
      finally { if (!cancelled) setLoading(false); }
    }
    loadFeed();
    return () => { cancelled = true; };
  }, [user, API_V2, navigate]);

  const handleEcho = (impulseText) => {
    navigate('/give', { state: { echoContext: impulseText } });
  };

  const giveFlowCarma = async (impulseId) => {
    try {
      const res = await axios.post(`${API_V2}/impulse/${impulseId}/carma`, {}, { withCredentials: true });
      setFeed((prev) => prev.map((item) => (
        item.id === impulseId
          ? { ...item, carmaCount: res.data.count, hasGivenCarma: true }
          : item
      )));
    } catch (err) {
      console.error('Flow carma failed:', err);
    }
  };

  const togglePinImpulse = async (impulseId) => {
    try {
      if (pinnedIds.includes(impulseId)) {
        await axios.delete(`${API_V2}/profile/pin-impulse/${impulseId}`, { withCredentials: true });
        setPinnedIds((prev) => prev.filter((x) => x !== impulseId));
        toast.success(t('innerstes.unpinSuccess'));
      } else {
        await axios.post(`${API_V2}/profile/pin-impulse`, { id: impulseId, type: 'flow' }, { withCredentials: true });
        setPinnedIds((prev) => [...prev, impulseId]);
        toast.success(t('innerstes.pinSuccess'));
      }
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  // Translate system impulse text and author labels
  const getTranslatedItem = (item) => {
    if (item.type === 'system') {
      // Extract index from id "sys-0", "sys-1" etc.
      const idx = parseInt(item.id.replace('sys-', ''), 10);
      const translatedText = Array.isArray(feedImpulses) && idx < feedImpulses.length
        ? feedImpulses[idx]
        : item.text;
      return { ...item, text: translatedText };
    }
    // Translate author labels for community items
    const translatedAuthor = item.anonymous
      ? t('feed.anonymous')
      : item.author === 'Jemand aus der Community'
        ? t('feed.communityMember')
        : item.author;
    return { ...item, author: translatedAuthor };
  };

  if (!user) return null;

  return (
    <div className="min-h-screen text-[var(--text)]">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8 space-y-6">
        {/* PHASE 0: StreakWidget geparkt — Komponente & API bleiben erhalten
        <StreakWidget API_V2={API_V2} /> */}

        {/* ── Herzensmensch reminder ── */}
        {reminder && !reminderDismissed && (
          <div className="bg-white/90 backdrop-blur-sm border border-[var(--primary)]/20 rounded-2xl p-5 shadow-sm" data-testid="feed-reminder-card">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[var(--primary)]/10 flex items-center justify-center flex-shrink-0">
                <HeartHandshake size={17} className="text-[var(--primary)]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold mb-0.5">{t('herzensmenschen.reminderTitle')}</p>
                <p className="text-xs text-[var(--muted)] leading-relaxed">
                  {reminder.daysSince != null
                    ? t('herzensmenschen.reminderText', { name: reminder.communityUserName, days: reminder.daysSince })
                    : t('herzensmenschen.reminderNever', { name: reminder.communityUserName })}
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/give', { state: { preselectUser: { id: reminder.communityUserId, name: reminder.communityUserName, initial: (reminder.communityUserName || '?')[0].toUpperCase() } } })}
                  data-testid="feed-reminder-carma-btn"
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] px-4 py-1.5 text-[11px] font-medium hover:opacity-90"
                >
                  <Send size={11} />
                  {t('herzensmenschen.sendCarma')}
                </button>
              </div>
              <button type="button" onClick={() => setReminderDismissed(true)} data-testid="feed-reminder-dismiss-btn" className="text-[var(--muted)] hover:text-[var(--text)] p-1 flex-shrink-0">
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {dailyImpulse && (
          <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-sm text-center space-y-4">
            <p className="text-xs uppercase tracking-wider text-[var(--muted)] font-medium">{t('feed.dailyImpulseLabel')}</p>
            <p className="text-lg sm:text-xl font-medium text-[var(--primary)] leading-relaxed">{dailyImpulse}</p>
            <button type="button" onClick={() => navigate('/give')} data-testid="daily-impulse-cta" className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium bg-[var(--primary)] text-[var(--primary-contrast)] hover:opacity-90">
              <Send size={15} />{t('feed.sendCarma')}
            </button>
          </div>
        )}

        {/* ── Inspiration Area ── */}
        {inspirationItems.length > 0 && (
          <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-5 sm:p-6 shadow-sm space-y-4" data-testid="inspiration-area">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-[var(--primary)]" />
                <h3 className="text-sm font-semibold">{t('feed.inspirationTitle')}</h3>
              </div>
              <button type="button" onClick={refreshInspiration} className="text-[10px] text-[var(--muted)] hover:text-[var(--text)] transition-colors" data-testid="refresh-inspiration-btn">
                {t('feed.inspirationMore')}
              </button>
            </div>
            <p className="text-[11px] text-[var(--muted)]">{t('feed.inspirationHint')}</p>
            <div className="space-y-2">
              {inspirationItems.map((prompt, idx) => (
                <div key={prompt} className="flex items-center gap-3 rounded-xl border border-[var(--border)] bg-white/70 px-4 py-3 group hover:border-[var(--primary)]/30 transition-colors">
                  <p className="flex-1 text-sm text-[var(--text)] leading-relaxed">{prompt}</p>
                  <button
                    type="button"
                    onClick={() => navigate('/give', { state: { echoContext: prompt } })}
                    data-testid={`inspiration-send-${idx}`}
                    className="flex-shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[10px] font-medium text-[var(--primary)] bg-[var(--primary)]/5 border border-[var(--primary)]/20 hover:bg-[var(--primary)]/10 transition-colors opacity-70 group-hover:opacity-100"
                  >
                    <Send size={10} />
                    {t('feed.inspirationSendCarma')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Tagebuch impulse ── */}
        <div className="bg-[var(--primary)]/5 backdrop-blur-sm border border-[var(--primary)]/10 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="feed-diary-impulse">
          <p className="flex-1 text-sm italic font-medium text-[var(--primary)] leading-relaxed">„{t('feed.diaryImpulse')}"</p>
          <button
            type="button"
            onClick={() => navigate('/tagebuch')}
            data-testid="feed-diary-impulse-btn"
            className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium text-[var(--primary)] bg-white/70 border border-[var(--primary)]/30 hover:bg-[var(--primary)]/10 transition-colors"
          >
            <BookHeart size={13} />
            {t('feed.diaryImpulseBtn')}
          </button>
        </div>

        {/* ── Gratitude Impulses ── */}
        {gratitude.length > 0 && (
          <div className="space-y-2.5" data-testid="feed-gratitude-section">
            <button
              type="button"
              onClick={() => setShowGratitude(!showGratitude)}
              className="flex items-center gap-2 text-sm font-semibold bg-white/70 backdrop-blur-sm rounded-full px-4 py-1.5"
            >
              <Heart size={14} className="text-[var(--primary)]" />
              {t('gratitude.feedTitle', 'Dankbarkeits-Impulse')} ({gratitude.length})
              {showGratitude ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showGratitude && (
              <div className="space-y-2">
                {gratitude.slice(0, 5).map(imp => (
                  <div key={imp.id} className="bg-white/85 backdrop-blur-sm rounded-2xl p-4 shadow-sm">
                    <p className="text-sm leading-relaxed"><span className="text-[var(--primary)] font-medium">Ich bin dankbar {imp.prefix || 'für'}</span> {imp.text}</p>
                    {imp.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {imp.tags.map(tag => (
                          <span key={tag} className="px-2 py-0.5 rounded-full text-[10px] bg-[var(--primary)]/8 text-[var(--primary)]">{tag}</span>
                        ))}
                      </div>
                    )}
                    <span className="text-[10px] text-[var(--muted)] mt-2 block">
                      {imp.author}{imp.createdAt && ` · ${new Date(imp.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`}
                    </span>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => navigate('/dankbar')}
                  className="w-full text-center text-xs text-[var(--primary)] hover:underline py-2"
                >
                  {t('gratitude.joinCta', 'Teile Deine Dankbarkeit')}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold bg-white/70 backdrop-blur-sm rounded-full px-4 py-1.5">{t('feed.title')}</h2>
          <button onClick={() => window.location.reload()} className="w-8 h-8 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors" title={t('feed.refresh')}><RefreshCw size={14} className="text-[var(--muted)]" /></button>
        </div>

        {loading ? (
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 text-center text-sm text-[var(--muted)]">{t('common.loading')}</div>
        ) : feed.length === 0 ? (
          <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-8 text-center space-y-4">
            <p className="text-sm text-[var(--muted)]">{t('feed.firstImpulse')}</p>
            <button type="button" onClick={() => navigate('/give')} className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium bg-[var(--primary)] text-[var(--primary-contrast)] hover:opacity-90">
              <Send size={15} />{t('feed.sendCarma')}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {feed.map((rawItem) => {
              const item = getTranslatedItem(rawItem);
              const isCommunity = item.type === 'community';
              const isSystemLike = item.type === 'system' || item.type === 'memento';
              const isPopular = isCommunity && !item.isTopOfWeek && (item.carmaCount || 0) >= 3;
              const cardClass = isSystemLike
                ? 'bg-[var(--primary)]/5 backdrop-blur-sm border border-[var(--primary)]/10'
                : item.isTopOfWeek
                  ? 'bg-white/95 backdrop-blur-sm border-2 border-amber-400/70 ring-1 ring-amber-200'
                  : isPopular
                    ? 'bg-white/90 backdrop-blur-sm border border-[var(--primary)]/30'
                    : 'bg-white/85 backdrop-blur-sm';
              return (
                <div key={item.id} className={`rounded-2xl p-5 shadow-sm transition-all ${cardClass}`}>
                  {item.isTopOfWeek && (
                    <div className="flex items-center gap-1.5 mb-2" data-testid={`top-of-week-${item.id}`}>
                      <Trophy size={13} className="text-amber-500" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">{t('feed.topOfWeek')}</span>
                    </div>
                  )}
                  {isPopular && (
                    <div className="flex items-center gap-1.5 mb-2" data-testid={`popular-badge-${item.id}`}>
                      <Heart size={12} className="text-[var(--primary)] fill-[var(--primary)]" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--primary)]">{t('feed.popular')}</span>
                    </div>
                  )}
                  {item.type === 'memento' && (
                    <div className="flex items-center gap-1.5 mb-2" data-testid={`memento-badge-${item.id}`}>
                      <BookHeart size={12} className="text-[var(--primary)]" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--primary)]/70">{t('feed.mementoLabel')}</span>
                    </div>
                  )}
                  <p className={`text-sm sm:text-base leading-relaxed ${isSystemLike ? 'text-[var(--primary)] italic font-medium' : 'text-[var(--text)]'}`}>
                    {isSystemLike ? `\u201E${item.text}\u201C` : item.text}
                  </p>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <span className="text-[10px] text-[var(--muted)]">
                      {item.author}
                      {item.createdAt && ` · ${new Date(item.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {isCommunity && (
                        item.isOwn || item.hasGivenCarma ? (
                          <span
                            data-testid={`flow-carma-count-${item.id}`}
                            className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-3 py-1 ${item.hasGivenCarma ? 'text-[var(--primary)] bg-[var(--primary)]/10' : 'text-[var(--muted)]'}`}
                          >
                            <Heart size={12} className={item.hasGivenCarma || item.carmaCount > 0 ? 'fill-[var(--primary)] text-[var(--primary)]' : ''} />
                            {item.carmaCount || 0}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => giveFlowCarma(item.id)}
                            data-testid={`flow-carma-btn-${item.id}`}
                            title={t('feed.giveCarmaHint')}
                            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[var(--primary)] hover:text-[var(--primary)]/80 transition-colors rounded-full px-3 py-1 hover:bg-[var(--primary)]/5 border border-[var(--primary)]/20"
                          >
                            <Heart size={12} />
                            {item.carmaCount > 0 ? item.carmaCount : t('feed.giveCarma')}
                          </button>
                        )
                      )}
                      {isCommunity && item.isOwn && (
                        <button
                          type="button"
                          onClick={() => togglePinImpulse(item.id)}
                          data-testid={`pin-flow-btn-${item.id}`}
                          className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-3 py-1 border transition-colors ${pinnedIds.includes(item.id) ? 'text-[var(--primary)] border-[var(--primary)]/50 bg-[var(--primary)]/10' : 'text-[var(--muted)] border-[var(--border)] hover:text-[var(--primary)]'}`}
                        >
                          <Pin size={12} className={pinnedIds.includes(item.id) ? 'fill-[var(--primary)]' : ''} />
                          {pinnedIds.includes(item.id) ? t('innerstes.pinned') : t('innerstes.pin')}
                        </button>
                      )}
                      {!item.isOwn && (
                        <button type="button" onClick={() => handleEcho(item.text)} data-testid={`echo-${item.id}`} className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[var(--primary)] hover:text-[var(--primary)]/80 transition-colors rounded-full px-3 py-1 hover:bg-[var(--primary)]/5">
                          <Send size={12} />{t('feed.echo')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
